import Check from '@mui/icons-material/Check';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Edit from '@mui/icons-material/Edit';
import Box from '@mui/material/Box';
import Grid, { gridClasses } from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { isNil } from 'lodash-es';
import { ReactNode, Ref, useMemo } from 'react';

import JobSummaryEditForm, {
  JobSummaryEditFormHandler,
} from '~components/Job/JobSummaryEditForm';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { PhaseSelection } from '~components/Order/PhaseSelection';
import { AddOnCharge } from '~hooks/useAddOns/models';
import { InvoiceLineItem } from '~hooks/useInvoiceLineItems/models';
import { Invoice } from '~hooks/useInvoices/models';
import { Job } from '~hooks/useJob/models';
import { PhaseBasic } from '~hooks/useProjects/models';
import { getLargestDeliveredQuantitySum } from '~utils/jobs/job-utils';

import JobMarginBreakdown from './JobMarginBreakdown';
import JobTimesheetSection from './JobTimesheetSection';

const NO_CONTENT = '-';

interface JobSummaryHeroProps {
  job: Job;
  companyId: string;
  payable: Invoice | null;
  receivable: Invoice | null;
  payableLineItems: InvoiceLineItem[];
  receivableLineItems: InvoiceLineItem[];
  payableAddOns?: AddOnCharge[];
  receivableAddOns?: AddOnCharge[];
  loadingInvoices?: boolean;
  // One-click edit affordances surfaced on each section (hours / receivable / payable).
  canEditHours?: boolean;
  onEditHours?: () => void;
  onEditReceivable?: () => void;
  onEditPayable?: () => void;
  // Loads stat becomes a button that opens the loads modal; approvedLoadsCount
  // (derived by the parent from the fetched loads) shows beneath it.
  onOpenLoads?: () => void;
  approvedLoadsCount?: number | null;
  // Inline (edit-in-place) summary editing — when true, the Start/End/Total fields in
  // column 1 and the Phase field become inputs at their display positions; the rest of
  // the hero stays visible. Wiring is owned by the parent (JobDetails).
  isEditing?: boolean;
  isSubmittingForm?: boolean;
  summaryFormRef?: Ref<JobSummaryEditFormHandler>;
  phaseOptions?: PhaseBasic[];
  selectedPhase?: PhaseBasic | null;
  onSelectPhase?: (phase: PhaseBasic | null) => void;
  // Inline-edit actions — Save/Cancel render directly beneath the column-1 form.
  onCancelEdit?: () => void;
  onSaveEdit?: () => void;
  // Refetch trigger after times are saved from the ticket drawer.
  onSummarySaved?: () => void | Promise<void>;
  // Opens the map + hours workspace (larger route replay beside the hour editor).
}

export default function JobSummaryHero({
  job,
  companyId,
  payable,
  receivable,
  payableLineItems,
  receivableLineItems,
  payableAddOns,
  receivableAddOns,
  loadingInvoices,
  canEditHours,
  onEditHours,
  onEditReceivable,
  onEditPayable,
  onOpenLoads,
  approvedLoadsCount,
  isEditing,
  isSubmittingForm,
  summaryFormRef,
  phaseOptions = [],
  selectedPhase,
  onSelectPhase,
  onCancelEdit,
  onSaveEdit,
  onSummarySaved,
}: JobSummaryHeroProps) {
  const theme = useTheme();
  const c = theme.brandV2.colors;

  // The Receivable/Payable reconciliation now lives in the summary's financial
  // column (no separate matrix section). Same instance is used by both variants.
  const breakdown = (
    <JobMarginBreakdown
      companyId={companyId}
      loadingInvoices={loadingInvoices}
      onEditPayable={onEditPayable}
      onEditReceivable={onEditReceivable}
      payable={payable}
      payableAddOns={payableAddOns}
      payableLineItems={payableLineItems}
      receivable={receivable}
      receivableAddOns={receivableAddOns}
      receivableLineItems={receivableLineItems}
    />
  );

  const stats = useMemo(() => {
    const {
      adjustedLoggingEndedAt,
      adjustedLoggingStartedAt,
      approvedWorkTimeMinutes,
      breakTimeMinutes,
      deliveredQuantities,
      firstGeofenceEntryAt,
      lastGeofenceExitAt,
      loggingEndedAt,
      loggingStartedAt,
      workTimeMinutes,
    } = job.jobSummary ?? {};

    // canonical hours: approved ?? work (== backend final_work_time_minutes used by per-hour billing)
    const effectiveWorkTime = approvedWorkTimeMinutes ?? workTimeMinutes;
    const totalHrs = isNil(effectiveWorkTime)
      ? NO_CONTENT
      : (effectiveWorkTime / 60).toFixed(2);

    const fmt = (d?: dayjs.Dayjs | null) =>
      d ? dayjs.tz(d).format('h:mm A') : NO_CONTENT;

    return {
      totalHrs,
      pausedMin: breakTimeMinutes ?? 0,
      scheduledStart: job.jobStartAt
        ? dayjs.tz(job.jobStartAt).format('h:mm A')
        : NO_CONTENT,
      started: fmt(adjustedLoggingStartedAt ?? loggingStartedAt),
      ended: fmt(adjustedLoggingEndedAt ?? loggingEndedAt),
      firstGeofenceEntry: fmt(firstGeofenceEntryAt),
      lastGeofenceExit: fmt(lastGeofenceExitAt),
      delivered: deliveredQuantities ?? [],
      // Raw values for the inline edit form (edit-in-place).
      startValue: adjustedLoggingStartedAt ?? loggingStartedAt ?? null,
      endValue: adjustedLoggingEndedAt ?? loggingEndedAt ?? null,
      workTimeValue: effectiveWorkTime ?? null,
    };
  }, [job.jobSummary, job.jobStartAt]);

  // Most-common unit of measure + its summed quantity (the dominant UoM across the
  // delivered quantities) — avoids summing across mixed units.
  const dominantDelivered = useMemo(
    () => getLargestDeliveredQuantitySum(job.jobSummary?.deliveredQuantities ?? []),
    [job.jobSummary?.deliveredQuantities],
  );
  const unitsTotal = dominantDelivered.total;
  const unitLabel = dominantDelivered.unitOfMeasure ?? job.unitOfMeasure?.name ?? '';

  // Job context relocated out of the header (driver/equipment there just repeated
  // the grid row; these three don't appear in the row, so they live here instead).
  const materialName = job.material?.name ?? NO_CONTENT;
  const phaseLabel = job.phase
    ? job.phase.code
      ? `${job.phase.name} (${job.phase.code})`
      : job.phase.name
    : NO_CONTENT;

  return (
    <Box>
      {/* ---- 3 equal columns with full-height dividers; fills the card (no own
             border/padding — the parent card is the boundary) ---- */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        }}
      >
        {/* 1 — Hours: total + the times that make it up + phase */}
        <Metric
          label={isEditing ? undefined : t('approvals.approve_jobs.summary_hero.hours')}
          action={
            isEditing ? null : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {canEditHours && onEditHours ? (
                  <Tooltip title={t('actions.edit')} placement="bottom">
                    <IconButton
                      aria-label={t('actions.edit')}
                      onClick={onEditHours}
                      size="small"
                      sx={{
                        p: 0.5,
                        border: `1px solid ${c.treadGray7}`,
                        borderRadius: '4px',
                        color: c.treadGray2,
                        '&:hover': {
                          borderColor: c.treadGray3,
                          backgroundColor: c.treadGray8,
                          color: c.treadBlack,
                        },
                      }}
                    >
                      <Edit sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                ) : null}
              </Box>
            )
          }
        >
          {isEditing ? (
            // Edit: form + phase dropdown + Save/Cancel, tightly stacked. No big total
            // and no "Hours" header, so the actions sit right under the edited fields.
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <JobSummaryEditForm
                ref={summaryFormRef}
                approvedWorkTimeMinutes={stats.workTimeValue}
                approvedWorkTimeMinutesText={`${t('approvals.driver_day.breaks')}: ${stats.pausedMin}min`}
                captionLabels
                isSubmittingForm={isSubmittingForm}
                loggingEndedAt={stats.endValue}
                loggingEndedAtText={`${t('approvals.driver_day.last_geofence_entry')}: ${stats.lastGeofenceExit}`}
                loggingStartedAt={stats.startValue}
                loggingStartedAtText={`${t('approvals.driver_day.first_geofence_entry')}: ${stats.firstGeofenceEntry}`}
              />
              {/* Phase Name + Phase Code — the same two-field selector the ticket
                  drawer uses (name filters the available codes). Stacked (not the
                  drawer's side-by-side) since this column is narrow — override the
                  component's built-in half-width fields to full width. */}
              <Grid
                container
                spacing={2}
                sx={{
                  [`& > .${gridClasses.root}`]: {
                    width: '100%',
                  },
                }}
              >
                <PhaseSelection
                  canAdd={false}
                  canEdit={false}
                  disableAutoDefaultedPhase
                  onChange={(o) =>
                    onSelectPhase?.(
                      o ? new PhaseBasic(o.id, o.name, o.code ?? null) : null,
                    )
                  }
                  phaseOptions={phaseOptions.map((p) => ({
                    id: p.id,
                    name: p.name,
                    code: p.code ?? undefined,
                  }))}
                  selectedPhase={
                    selectedPhase
                      ? {
                          id: selectedPhase.id,
                          name: selectedPhase.name,
                          code: selectedPhase.code ?? undefined,
                        }
                      : null
                  }
                />
              </Grid>
              {/* Save / Cancel — directly beneath the form, not in a distant footer */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <SmallButton
                  color="inherit"
                  disableElevation
                  onClick={onCancelEdit}
                  size="small"
                  variant="outlined"
                  sx={{ border: `1px solid ${c.treadGray7}` }}
                >
                  {t('actions.cancel')}
                </SmallButton>
                <SmallLoadingButton
                  color="brandV2Green"
                  loading={isSubmittingForm}
                  onClick={onSaveEdit}
                  size="small"
                  startIcon={<Check />}
                  variant="contained"
                  sx={{ fontWeight: 700 }}
                >
                  {t('actions.save')}
                </SmallLoadingButton>
              </Box>
            </Box>
          ) : (
            <>
              <Box>
                {/* Total is the key figure (large); times scan as one right column below. */}
                <LabelValueRow
                  noTopBorder
                  large
                  label={t('common.total')}
                  value={stats.totalHrs}
                />
                <LabelValueRow
                  label={t('approvals.approve_jobs.summary_hero.start')}
                  value={stats.started}
                  subs={[
                    {
                      label: t('approvals.driver_day.first_geofence_entry'),
                      value: stats.firstGeofenceEntry,
                    },
                    {
                      label: t('approvals.approve_jobs.summary_hero.scheduled_start'),
                      value: stats.scheduledStart,
                    },
                  ]}
                />
                <LabelValueRow
                  label={t('approvals.approve_jobs.summary_hero.end')}
                  value={stats.ended}
                  subs={[
                    {
                      label: t('approvals.driver_day.last_geofence_entry'),
                      value: stats.lastGeofenceExit,
                    },
                  ]}
                />
                <LabelValueRow
                  label={t('job_events.paused')}
                  value={`${stats.pausedMin}m`}
                />
                <LabelValueRow
                  label={t('approvals.approve_jobs.summary_hero.phase')}
                  value={phaseLabel}
                />
              </Box>
              <Box sx={{ mt: 1.5 }}>
                <JobTimesheetSection job={job} onSummarySaved={onSummarySaved} />
              </Box>
            </>
          )}
        </Metric>

        {/* 2 — Delivered Totals (loads · tickets · units) + the job context
            relocated from the header (material / phase / route) */}
        <Metric label={t('approvals.approve_jobs.summary_hero.delivered_totals')} divider>
          <Box>
            {/* Key delivery figures large; Material as a small supporting row. */}
            <LabelValueRow
              noTopBorder
              large
              label={t('order.form.loads')}
              value={job.completedLoadsCount ?? 0}
              onClick={onOpenLoads}
              note={
                typeof approvedLoadsCount === 'number'
                  ? `${approvedLoadsCount} ${t('approvals.driver_day.approved').toLowerCase()}`
                  : undefined
              }
            />
            <LabelValueRow
              large
              label={t('approvals.approve_jobs.summary_hero.tickets')}
              value={job.ticketsCount ?? 0}
            />
            <LabelValueRow
              large
              label={unitLabel || t('form_fields.units')}
              value={unitsTotal ? unitsTotal.toFixed(2) : NO_CONTENT}
            />
            <LabelValueRow label={t('form_fields.material')} value={materialName} />
          </Box>
        </Metric>

        {/* 3 — Billing: Receivable / Payable reconciliation */}
        <Metric divider label={t('approvals.driver_day.billing')}>
          {breakdown}
        </Metric>
      </Box>
    </Box>
  );
}

/* ---------- presentational helpers ---------- */

function Metric({
  label,
  action,
  divider,
  children,
}: {
  label?: ReactNode;
  action?: ReactNode;
  divider?: boolean;
  children: ReactNode;
}) {
  const theme = useTheme();
  const c = theme.brandV2.colors;
  return (
    <Box
      sx={{
        px: 2.5,
        py: 2.5,
        minWidth: 0,
        position: 'relative',
        ...(divider && {
          borderLeft: `1px solid ${c.treadGray7}`,
        }),
      }}
    >
      {label && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            mb: 1.75,
          }}
        >
          {/* Column header — Metrics-tab card-title style (sentence case, dark). */}
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 600,
              color: c.treadBlack,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            {label}
          </Typography>
          {action}
        </Box>
      )}
      {children}
    </Box>
  );
}

// The single row primitive for all three columns: label left, value right-aligned so
// the numbers scan as one column. `large` renders the key figure big (Billing-total
// treatment) with an eyebrow label; supporting rows stay small.
function LabelValueRow({
  label,
  value,
  unit,
  subs,
  note,
  large,
  noTopBorder,
  onClick,
}: {
  label: string;
  value: string | number;
  unit?: string;
  subs?: { label: string; value: string }[];
  note?: string;
  large?: boolean;
  noTopBorder?: boolean;
  onClick?: () => void;
}) {
  const theme = useTheme();
  const c = theme.brandV2.colors;
  const clickable = Boolean(onClick);
  // Every row label is styled the same (Start/End/Paused): 12px, 600, treadGray2,
  // sentence case. Only the VALUE size conveys hierarchy (large vs small).
  const labelSx = { fontSize: '12px', fontWeight: 600, color: c.treadGray2 };
  const valueSx = large
    ? {
        fontSize: '24px',
        fontWeight: 700,
        letterSpacing: '-.02em',
        lineHeight: 1.05,
        color: clickable ? c.treadOrangeDark : c.treadBlack,
        fontVariantNumeric: 'tabular-nums' as const,
        ...(clickable && { textDecoration: 'underline', textUnderlineOffset: '3px' }),
      }
    : {
        fontSize: '13px',
        fontWeight: 700,
        color: c.treadBlack,
        fontVariantNumeric: 'tabular-nums' as const,
      };

  const content = (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Typography sx={labelSx}>{label}</Typography>
        <Box sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.5 }}>
          <Typography sx={valueSx}>{value}</Typography>
          {unit && (
            <Typography
              sx={{
                fontSize: large ? '14px' : '11px',
                fontWeight: 500,
                color: c.treadGray3,
              }}
            >
              {unit}
            </Typography>
          )}
        </Box>
      </Box>
      {note && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 0.5,
            mt: 0.25,
          }}
        >
          <CheckCircle sx={{ fontSize: 16, color: c.treadGreen }} />
          <Typography sx={{ fontSize: '13px', fontWeight: 600, color: c.treadGray1 }}>
            {note}
          </Typography>
        </Box>
      )}
      {/* Sub-lines: label left, value right so the times align in the same column. */}
      {subs?.map((s) => (
        <Box
          key={s.label}
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: 1,
            mt: 0.125,
          }}
        >
          <Typography sx={{ fontSize: '10px', color: c.treadGray3 }}>
            {s.label}
          </Typography>
          <Typography
            sx={{
              fontSize: '10px',
              color: c.treadGray3,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {s.value}
          </Typography>
        </Box>
      ))}
    </>
  );

  const topBorder = noTopBorder ? 'none' : `1px solid ${c.treadGray7}`;

  if (!clickable) {
    return <Box sx={{ py: 0.625, borderTop: topBorder }}>{content}</Box>;
  }
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        all: 'unset',
        display: 'block',
        width: '100%',
        cursor: 'pointer',
        borderTop: topBorder,
        py: 0.625,
        borderRadius: '4px',
        transition: 'background-color 120ms ease',
        // Clickable cue: blue underlined value + the whole row highlights on hover.
        '&:hover': { backgroundColor: c.treadGray8 },
        '&:focus-visible': {
          outline: `2px solid ${c.treadOrangeDark}`,
          outlineOffset: '2px',
        },
      }}
    >
      {content}
    </Box>
  );
}
